import type { AnchorHTMLAttributes, ReactNode } from "react"
import Link from "next/link"

import { isSafeSofiaAppHref, isSafeSofiaHref } from "../utils/sofiaHref"
import { CoviCodeBlock } from "./CoviCodeBlock"

/** Relative app path or same-origin URL: navigates in place, never leaves the app. */
function isInAppHref(href: string): boolean {
  if (isSafeSofiaAppHref(href)) {
    return true
  }
  if (typeof window === "undefined") {
    return false
  }
  try {
    return new URL(href).origin === window.location.origin
  } catch {
    return false
  }
}

/**
 * Link renderer for Sofia's markdown. In-app links (a saved notebook, a run)
 * navigate client-side; external links open in a new tab. Replaces the
 * renderer's default anchor, which treats every link as external and asks
 * before opening it.
 */
export function SofiaMarkdownLink({
  href,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode }) {
  if (!href) {
    return <span>{children}</span>
  }
  if (!isSafeSofiaHref(href)) {
    return <span className={rest.className}>{children}</span>
  }
  if (isInAppHref(href)) {
    return (
      <Link href={href} className={rest.className}>
        {children}
      </Link>
    )
  }
  return (
    <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

/** Component overrides every Sofia markdown surface shares. */
export const SOFIA_MARKDOWN_COMPONENTS = {
  pre: CoviCodeBlock,
  a: SofiaMarkdownLink,
}
